import React from 'react';
import { Box, Text, HStack, VStack, Avatar, Badge } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, User } from '../types';
import { useAuth } from '../context/AuthContext';

interface TaskCardProps {
  task: Task;
  statusLabel?: string;
  onClick: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, statusLabel, onClick }) => {
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';
  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const userId = user?.id;

  const participants: User[] = [];
  if (Array.isArray(task.participants) && task.participants.length > 0) {
    participants.push(
      ...task.participants.filter(
        (p) => p && (p.id || (p as any)._id)
      )
    );
  }
  if (task.assignedTo && !participants.some((p) => (p.id || p._id) === (task.assignedTo as any).id || (task.assignedTo as any)._id)) {
    participants.push(task.assignedTo);
  }
  if (
    task.createdBy &&
    !participants.some(
      (p) => (p.id || p._id) === (task.createdBy?.id || (task.createdBy as any)?._id)
    )
  ) {
    participants.push(task.createdBy);
  }

  const canEdit =
    !!isAdmin ||
    !!isMasterAdmin ||
    !!(
      userId &&
      participants.some(
        (p) => (p.id || p._id)?.toString() === userId
      )
    );
  const displayParticipants = participants.slice(0, 3);
  const extraCount = participants.length > 3 ? participants.length - 3 : 0;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task._id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getInitials = (user: User) => (user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase() || '?').slice(0, 2);

  // Handle click - only trigger if not dragging
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if we're in the middle of a drag
    if (!isDragging) {
      onClick();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      bg="white"
      p={{ base: 3, md: 5 }}
      borderRadius="lg"
      borderWidth={1}
      borderColor="gray.200"
      position="relative"
      _hover={{ 
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
        borderColor: 'blue.300',
      }}
      boxShadow="sm"
      transition="all 0.2s"
    >
      {canEdit && (
      <Box
        {...listeners}
        {...attributes}
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        width="20px"
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ bg: 'gray.100' }}
        borderRadius="lg 0 0 lg"
      >
        <Box
          width="4px"
          height="40px"
          bg="gray.300"
          borderRadius="2px"
          _hover={{ bg: 'blue.400' }}
        />
      </Box>
      )}
      {/* Clickable content area */}
      <Box
        onClick={handleClick}
        cursor="pointer"
        pl={canEdit ? 6 : 5}
      >
        <Text fontWeight="semibold" mb={3} color="gray.800" fontSize={{ base: 'sm', md: 'md' }}>
          {task.title}
        </Text>
        <VStack align="stretch" gap={2}>
          {statusLabel && (
            <HStack gap={1} fontSize="xs" color="gray.600">
              <Text fontWeight="medium">Status:</Text>
              <Badge size="sm" variant="subtle" colorScheme="gray">{statusLabel}</Badge>
            </HStack>
          )}
          {(task.dependsOn?.length ?? 0) > 0 && (
            <HStack gap={1} fontSize="xs" color="gray.600">
              <Text fontWeight="medium">Depends on:</Text>
              <Text>{task.dependsOn!.length} {task.dependsOn!.length === 1 ? 'task' : 'tasks'}</Text>
            </HStack>
          )}
          <HStack gap={2} alignItems="center" fontSize="xs" color="gray.600">
            <Text fontWeight="medium">Participants:</Text>
            <HStack gap={1}>
              {displayParticipants.length > 0 ? (
                <>
                  {displayParticipants.map((u) => {
                    const key = u.id || (u as any)._id;
                    return (
                      <Avatar.Root key={key} size="xs">
                        <Avatar.Fallback bg="gray.200" color="black" fontWeight="bold" fontSize="2xs">
                          {getInitials(u)}
                        </Avatar.Fallback>
                      </Avatar.Root>
                    );
                  })}
                  {extraCount > 0 && (
                    <Badge
                      as="span"
                      fontSize="xs"
                      bg="gray.200"
                      color="black"
                      borderRadius="full"
                      px={2}
                      title={participants
                        .slice(3)
                        .map((u) => u.name)
                        .filter(Boolean)
                        .join(', ') || undefined}
                    >
                      +{extraCount}
                    </Badge>
                  )}
                </>
              ) : (
                <Text fontStyle="italic">—</Text>
              )}
            </HStack>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default TaskCard;
